/**
 * Travel Analytics Store
 *
 * Zustand store for managing global analytics state
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DATE_RANGE_PRESETS, type DateRangePreset } from "../constants";
import type { Product, ITravelAnalyticsFilter } from "../models/IAnalyticsTypes";

interface IAnalyticsState {
  /** Current date range preset */
  datePreset: DateRangePreset;
  /** Custom date range (when preset is 'custom') */
  customDateRange: {
    startDate: string;
    endDate: string;
  } | null;
  /** Selected products filter */
  selectedProducts: Product[];
  /** Comparison with previous period enabled */
  compareWithPrevious: boolean;
  /** Comparison baseline for deltas: previous period or forecast */
  comparisonMode: "previous" | "forecast";
  /** Last refresh timestamp */
  lastRefresh: number;
}

interface IAnalyticsActions {
  /** Set date range preset */
  setDatePreset: (preset: DateRangePreset) => void;
  /** Set custom date range */
  setCustomDateRange: (range: { startDate: string; endDate: string }) => void;
  /** Set selected products */
  setSelectedProducts: (products: Product[]) => void;
  /** Toggle comparison mode */
  toggleComparison: () => void;
  /** Set the comparison baseline (previous period / forecast) */
  setComparisonMode: (mode: "previous" | "forecast") => void;
  /** Trigger refresh */
  refresh: () => void;
  /** Reset filters to defaults */
  resetFilters: () => void;
  /** Get filter input for GraphQL queries */
  getFilterInput: () => ITravelAnalyticsFilter;
}

const initialState: IAnalyticsState = {
  datePreset: DATE_RANGE_PRESETS.LAST_7_DAYS,
  customDateRange: null,
  selectedProducts: [],
  compareWithPrevious: true,
  comparisonMode: "previous",
  lastRefresh: Date.now(),
};

/**
 * Calculate date range from preset
 */
export function getDateRangeFromPreset(preset: DateRangePreset): {
  startDate: string;
  endDate: string;
} {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  // Last representable instant of today (local). Rolling presets end here so
  // that events that happened earlier today are included; ending at
  // startOfToday would silently drop everything since local midnight.
  const endOfToday = new Date(startOfToday.getTime() + DAY_MS - 1);

  let startDate: Date;
  let endDate = endOfToday;

  switch (preset) {
    case DATE_RANGE_PRESETS.TODAY:
      startDate = startOfToday;
      break;

    case DATE_RANGE_PRESETS.YESTERDAY:
      startDate = new Date(startOfToday.getTime() - DAY_MS);
      endDate = new Date(startOfToday.getTime() - 1);
      break;

    // "Last N days" is inclusive of today: subtract N-1 so the window spans
    // exactly N calendar days ending tonight (e.g. 7 days = today + prior 6).
    case DATE_RANGE_PRESETS.LAST_7_DAYS:
      startDate = new Date(startOfToday.getTime() - 6 * DAY_MS);
      break;

    case DATE_RANGE_PRESETS.LAST_14_DAYS:
      startDate = new Date(startOfToday.getTime() - 13 * DAY_MS);
      break;

    case DATE_RANGE_PRESETS.LAST_30_DAYS:
      startDate = new Date(startOfToday.getTime() - 29 * DAY_MS);
      break;

    case DATE_RANGE_PRESETS.THIS_MONTH:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;

    case DATE_RANGE_PRESETS.PREVIOUS_MONTH:
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      // Last ms of the previous month (start of this month minus 1ms).
      endDate = new Date(new Date(now.getFullYear(), now.getMonth(), 1).getTime() - 1);
      break;

    case DATE_RANGE_PRESETS.LAST_3_MONTHS:
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      break;

    case DATE_RANGE_PRESETS.YEAR_TO_DATE:
      startDate = new Date(now.getFullYear(), 0, 1);
      break;

    default:
      startDate = new Date(startOfToday.getTime() - 6 * DAY_MS);
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

export const useAnalyticsStore = create<IAnalyticsState & IAnalyticsActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setDatePreset: (preset) =>
        set({ datePreset: preset, customDateRange: null }),

      setCustomDateRange: (range) =>
        set({
          datePreset: DATE_RANGE_PRESETS.CUSTOM,
          customDateRange: range,
        }),

      setSelectedProducts: (products) => set({ selectedProducts: products }),

      toggleComparison: () =>
        set((state) => ({ compareWithPrevious: !state.compareWithPrevious })),

      setComparisonMode: (mode) => set({ comparisonMode: mode }),

      refresh: () => set({ lastRefresh: Date.now() }),

      resetFilters: () => set(initialState),

      getFilterInput: () => {
        const state = get();
        const dateRange =
          state.datePreset === DATE_RANGE_PRESETS.CUSTOM && state.customDateRange
            ? state.customDateRange
            : getDateRangeFromPreset(state.datePreset);

        return {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          products:
            state.selectedProducts.length > 0
              ? state.selectedProducts
              : undefined,
          compareWithPrevious: state.compareWithPrevious,
          granularity: "DAY" as const,
          timezone: "America/Mexico_City",
        };
      },
    }),
    {
      name: "ticketon-analytics-filters",
      partialize: (state) => ({
        datePreset: state.datePreset,
        customDateRange: state.customDateRange,
        selectedProducts: state.selectedProducts,
        compareWithPrevious: state.compareWithPrevious,
        comparisonMode: state.comparisonMode,
      }),
    }
  )
);

/**
 * Hook to get the computed date range
 */
export function useDateRange() {
  const datePreset = useAnalyticsStore((state) => state.datePreset);
  const customDateRange = useAnalyticsStore((state) => state.customDateRange);

  if (datePreset === DATE_RANGE_PRESETS.CUSTOM && customDateRange) {
    return customDateRange;
  }

  return getDateRangeFromPreset(datePreset);
}
